'use client';

import {upperFirst} from 'lodash';
import {useTranslations} from 'next-intl';
import {useMemo} from 'react';
import {z} from 'zod';
import {Close as IconClose} from '../../../../../components/icons';
import useShop from '../../../../../stores/shop/useContext';
import {ProductFiltersSchema} from './filters.dto';
import useFilters from './hooks/useFilters';

function ActiveFilters() {
  const t = useTranslations('shop');

  const {facets} = useShop((s) => s);
  const {filters, set, del, reset} = useFilters();

  const activeFilters = useMemo(() => {
    const output: Array<{
      label: string;
      slug: string;
      value: string | string[];
      filterKey: keyof z.infer<typeof ProductFiltersSchema>;
    }> = [];
    if (filters.category && filters.category.length > 0) {
      output.push(
        ...filters.category.map((c) => {
          const facet = facets.categories.find((f) => f.slug === c);
          return {
            label: t('filter.category.title'),
            slug: c,
            value: upperFirst(facet?.name || c),
            filterKey: 'category' as const,
          };
        }),
      );
    }
    if (filters.subcategory && filters.subcategory.length > 0) {
      output.push(
        ...filters.subcategory.map((c) => {
          const facet = facets.categories
            .flatMap((f) => f.children)
            .find((f) => f.slug === c);
          return {
            label: t('filter.subcategory.title'),
            slug: c,
            value: upperFirst(facet?.name || c),
            filterKey: 'subcategory' as const,
          };
        }),
      );
    }
    if (filters.country && filters.country.length > 0) {
      output.push(
        ...filters.country.map((c) => {
          const facet = facets.countries.find((f) => f.slug === c);
          return {
            label: t('filter.country.title'),
            slug: c,
            value: facet?.name || c,
            filterKey: 'country' as const,
          };
        }),
      );
    }
    if (filters.region && filters.region.length > 0) {
      output.push(
        ...filters.region.map((c) => {
          const facet = facets.countries
            .flatMap((f) => f.children)
            .find((f) => f.slug === c);
          return {
            label: t('filter.country.title'),
            slug: c,
            value: facet?.name || c,
            filterKey: 'region' as const,
          };
        }),
      );
    }
    if (filters.characteristic && filters.characteristic.length > 0) {
      output.push(
        ...filters.characteristic.map((c) => {
          const facet = facets.characteristics.find((f) => f.slug === c);
          return {
            label: t('filter.characteristic.title'),
            slug: c,
            value: facet?.name || c,
            filterKey: 'characteristic' as const,
          };
        }),
      );
    }
    if (filters.occasion && filters.occasion.length > 0) {
      output.push(
        ...filters.occasion.map((c) => {
          const facet = facets.occasions
            .reduce((acc, curr) => [...acc, curr, ...curr.children], [])
            .find((f) => f.slug === c);
          return {
            label: t('filter.occasion.title'),
            slug: c,
            value: facet?.name || c,
            filterKey: 'occasion' as const,
          };
        }),
      );
    }
    if (filters.tag && filters.tag.length > 0) {
      output.push(
        ...filters.tag.map((tag) => {
          return {
            label: 'Tag',
            slug: tag,
            value: tag,
            filterKey: 'tag' as const,
          };
        }),
      );
    }
    return output;
  }, [filters]);

  const activePriceFilter = useMemo(() => {
    if (!!filters.priceMin) {
      return !!filters.priceMax
        ? t('filter.price.fromTo', {
            from: filters.priceMin,
            to: filters.priceMax,
          })
        : t('filter.price.from', {from: filters.priceMin});
    }
    if (!!filters.priceMax) {
      return t('filter.price.to', {to: filters.priceMax});
    }
  }, [filters.priceMin, filters.priceMax]);

  if (activeFilters.length === 0 && !activePriceFilter) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-2">
      <button
        className="inline-flex items-center py-2 px-3 gap-2 rounded-full bg-greyscale-offwhite border border-greyscale-separator text-sm tracking-tight text-primary font-medium"
        onClick={() => reset()}
      >
        {t('filter.resetAll')}
      </button>
      {activeFilters.map((item) => (
        <button
          key={`${item.filterKey}_${item.slug}`}
          className="inline-flex items-center py-2 px-3 gap-2 rounded-full bg-greyscale-offwhite border border-greyscale-separator text-sm tracking-tight text-greyscale-label"
          onClick={() => {
            const current = activeFilters.find(
              (f) => f.filterKey === item.filterKey,
            );
            if (
              current &&
              Array.isArray(current.value) &&
              current.value.length > 1
            ) {
              set({[item.filterKey]: item.value});
            } else {
              del(item.filterKey);
            }
          }}
        >
          <div className="space-x-1">
            <span className="font-medium">{item.label}:</span>
            <span className="font-bold text-greyscale-body">
              {item.value}
            </span>
          </div>
          <IconClose className="w-4 h-4" />
        </button>
      ))}
      {!!activePriceFilter && (
        <button
          className="inline-flex items-center py-2 px-3 gap-2 rounded-full bg-greyscale-offwhite border border-greyscale-separator text-sm tracking-tight text-greyscale-label"
          onClick={() => {
            del('priceMin', 'priceMax');
          }}
        >
          <div className="space-x-1">
            <span className="font-medium">{t('filter.price.title')}:</span>
            <span className="font-bold text-greyscale-body">
              {activePriceFilter}
            </span>
          </div>
          <IconClose className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

export default ActiveFilters;
